import type { FleetUnitVehicle } from '@chukua/common';
import { Tabs, Tag } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { VehicleRigPanel } from '../../../components/VehicleRigPanel';
import { useTruckCatalogues } from '../../trucks/hooks/use-truck-catalogues';
import { useTruckDetail } from '../../trucks/hooks/use-truck-detail';
import { useTruckPermissions } from '../../trucks/hooks/use-truck-permissions';
import { TRUCK_STATUS_COLORS, TRUCK_STATUS_LABELS } from '../../trucks/policies/truck-status-labels';
import type { TruckDetail } from '../../trucks/trucks.api';
import { isMaintenanceTab, UNIT_TABS, unitPath, type UnitTab } from '../units.paths';
import { buildUnitTabs } from './build-unit-tabs';
import { UNITS_TRAIL } from './units-trail';

interface UnitDetailProps {
  truck: FleetUnitVehicle;
  trailer: FleetUnitVehicle | null;
  tab: UnitTab;
  /** After a couple, swap or drop: the unit's trailer has changed. */
  onUnitChanged: () => void;
}

function describe(truck: TruckDetail, trailer: FleetUnitVehicle | null): string {
  const vehicle = `${truck.make} ${truck.model}`;
  return trailer ? `With trailer ${trailer.registration} · ${vehicle}` : vehicle;
}

/**
 * A unit's page (gitaumoses4/chukua#390): the truck it is named by, the rig drawn with couple, swap and drop, and tabs that show
 * the truck and its trailer together. Permissions are the truck page's: `truck.manage` edits both vehicles and the coupling,
 * `document.manage` their papers, `maintenance.view` opens the maintenance tabs (UX only; the server stays the authority).
 */
export function UnitDetail({ truck: unitTruck, trailer, tab, onUnitChanged }: UnitDetailProps) {
  const navigate = useNavigate();
  const detail = useTruckDetail(unitTruck.id);
  const catalogues = useTruckCatalogues();
  const { isKnown, canManageTruck, canManageDocuments, canViewMaintenance } = useTruckPermissions();
  const [couplingVersion, setCouplingVersion] = useState(0);
  const [rigVersion, setRigVersion] = useState(0);

  if (isMaintenanceTab(tab) && isKnown && !canViewMaintenance) return <Navigate to={unitPath(unitTruck.registration, 'details')} replace />;
  if (detail.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (detail.state.status === 'loading') return <DetailPageSkeleton label="Loading unit" breadcrumbs={UNITS_TRAIL} tabs={UNIT_TABS.length} />;
  if (detail.state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Truck not found" description="This truck doesn't exist, or the link is out of date." />;
  }
  if (detail.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this unit" description="Something went wrong loading this unit. Try reloading the page." />;
  }

  const { truck } = detail.state;
  const truckClasses = catalogues.status === 'loaded' ? catalogues.truckClasses : [];

  return (
    <>
      <PageHeader
        breadcrumbs={UNITS_TRAIL}
        title={truck.registration}
        description={describe(truck, trailer)}
        actions={<Tag color={TRUCK_STATUS_COLORS[truck.status]}>{TRUCK_STATUS_LABELS[truck.status]}</Tag>}
      />
      {/* Keyed on what the rig is drawn from, so a new class, type or colour on either vehicle redraws it. */}
      <VehicleRigPanel
        key={`${truck.updatedAt}:${rigVersion}`}
        vehicleKind="truck"
        vehicleId={truck.id}
        canManage={canManageTruck}
        onChanged={() => {
          setCouplingVersion((version) => version + 1);
          onUnitChanged();
        }}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(unitPath(truck.registration, key as UnitTab))}
        items={buildUnitTabs({
          truck,
          trailer,
          truckClasses,
          canManageTruck,
          canManageDocuments,
          canViewMaintenance,
          detail,
          couplingVersion,
          onTrailerSaved: () => setRigVersion((version) => version + 1),
        })}
      />
    </>
  );
}
